// VERSION MARKER: Cache-First Loading v3.2
// If you see this log, the new code with cache-first loading is loaded

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractVideoId, isValidYouTubeInput } from '../utils/youtubeUtils';
import { fetchVideoComments } from '../services/youtubeService';
import { analyzeCommentsWithLearning } from '../services/learningSystem';
import { saveToHistory } from '../services/historyService';
import { saveAnalysis, loadAnalysis } from '../services/analysisStorage';
import { canAnalyzeVideo } from '../services/planGatingService';
import { useAuth } from '../contexts/AuthContext';
import { validateCachedData } from '../utils/validateAnalysisData';
import CommentCard from '../components/CommentCard';
import VideoInfo from '../components/VideoInfo';
import SummaryPanel from '../components/SummaryPanel';
import { AlertTriangle } from 'react-feather';
import { FiYoutube } from 'react-icons/fi';

const HOMEPAGE_VERSION = 'v3.2';
const HOMEPAGE_VERSION_TIMESTAMP = new Date().toISOString();
console.log(`­ƒÜÇ [HomePage ${HOMEPAGE_VERSION}] Cache-first loading enabled at ${HOMEPAGE_VERSION_TIMESTAMP} - analyzed videos will load instantly`);
console.log(`­ƒÜÇ [HomePage ${HOMEPAGE_VERSION}] CRITICAL: If you see "Ô£à [CommentSignals] Saved signal" logs, the browser is serving OLD CODE`);
console.log(`­ƒÜÇ [HomePage ${HOMEPAGE_VERSION}] ACTION REQUIRED: Hard refresh your browser (Ctrl+Shift+R or Cmd+Shift+R)`);
console.log(`­ƒÜÇ [HomePage ${HOMEPAGE_VERSION}] This version includes pre-check to prevent redundant analysis even if cache fails`);

const HomePage = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [videoMetadata, setVideoMetadata] = useState(null);
  const [analysisDate, setAnalysisDate] = useState(null);
  const [activeTab, setActiveTab] = useState('buyers');
  const [comments, setComments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const commentsPerPage = 10;

  // Clear analysis when navigating to homepage (always show empty state)
  useEffect(() => {
    // Reset to empty state when component mounts or route changes to /
    if (location.pathname === '/') {
      setAnalysis(null);
      setVideoMetadata(null);
      setVideoUrl('');
      setError(null);
      setComments([]);
      setActiveTab('buyers');
      setCurrentPage(1);
      setLoading(false);
    }
  }, [location.pathname]);

  // Load video URL from location state or URL params (when clicking from history/folders)
  useEffect(() => {
    if (!currentUser?.uid || location.pathname !== '/') {
      return; // Don't load if user is not signed in or not on homepage
    }

    // Check URL params first (e.g., /?v=videoId)
    const searchParams = new URLSearchParams(location.search);
    const videoIdFromUrl = searchParams.get('v');
    
    if (videoIdFromUrl) {
      // Convert videoId to full URL for consistency
      const url = `https://www.youtube.com/watch?v=${videoIdFromUrl}`;
      setVideoUrl(url);
      // Clear URL param to prevent re-loading on navigation
      navigate(location.pathname, { replace: true });
      // Load the video with cache check (should be instant if cached)
      handleLoadVideo(url).catch(err => {
        console.error('Error loading video:', err);
        setError(err.message || 'Failed to load video');
        setLoading(false);
      });
      return;
    }
    
    // Fallback to location state (e.g., from history)
    if (location.state?.videoUrl) {
      const url = location.state.videoUrl;
      setVideoUrl(url);
      // Clear state to prevent re-loading on navigation
      navigate(location.pathname, { replace: true, state: {} });
      // Load the video with cache check (should be instant if cached)
      handleLoadVideo(url).catch(err => {
        console.error('Error loading video:', err);
        setError(err.message || 'Failed to load video');
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search, currentUser?.uid]);

  // Handle loading a video (with caching)
  const handleLoadVideo = async (url) => {
    if (!url || !isValidYouTubeInput(url)) {
      setError('Invalid YouTube URL');
      return;
    }

    const videoId = extractVideoId(url);
    if (!videoId) {
      setError('Could not extract video ID from URL');
      return;
    }
    
    if (!currentUser?.uid) {
      setError('Please sign in to load videos');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // CRITICAL: Check cache FIRST (should be instant from localStorage - < 1ms)
      console.log(`­ƒöì [CACHE CHECK ${HOMEPAGE_VERSION}] Starting cache check for videoId: ${videoId}, userId: ${currentUser.uid}`);
      const startTime = performance.now();
      
      // Check localStorage cache FIRST (synchronous, instant - no network calls)
      const cached = await loadAnalysis(currentUser.uid, videoId);
      const loadTime = performance.now() - startTime;
      
      console.log(`­ƒöì [CACHE CHECK ${HOMEPAGE_VERSION}] Cache result: ${!!cached}, Analysis: ${!!cached?.analysis}, Metadata: ${!!cached?.videoMetadata}, Load time: ${loadTime.toFixed(2)}ms`);
      
      if (cached && cached.analysis && cached.videoMetadata) {
        // Validate cached data before using it
        const validated = validateCachedData(cached);
        if (validated) {
          // Cache found and validated - load instantly (synchronous state updates)
          console.log(`ÔÜí [INSTANT LOAD] Cached video loaded in ${loadTime.toFixed(2)}ms for ${videoId}`);
          console.log(`ÔÜí [CACHE] Analysis exists: ${!!validated.analysis}, Metadata exists: ${!!validated.videoMetadata}, Comments: ${validated.comments?.length || 0}`);
          setAnalysis(validated.analysis);
          setVideoMetadata(validated.videoMetadata);
          setComments(validated.comments || []);
          setAnalysisDate(validated.analyzedAt ? new Date(validated.analyzedAt) : new Date());
          setActiveTab('buyers');
          setLoading(false);
          // CRITICAL: Return early - DO NOT call handleAnalyze
          console.log(`Ô£à [CACHE CHECK ${HOMEPAGE_VERSION}] Returning early - analysis prevented`);
          return;
        } else {
          console.warn(`ÔØî [CACHE CHECK ${HOMEPAGE_VERSION}] Cached data failed validation, proceeding with fresh analysis`);
        }
      }

      // No cache found - proceed with analysis
      // Note: handleAnalyze will check for signals and skip redundant saving
      console.log(`ÔÜá´©Å [NO CACHE] Cache not found for ${videoId} (cached: ${!!cached}, analysis: ${!!cached?.analysis}, metadata: ${!!cached?.videoMetadata}), starting full analysis...`);
      // Note: handleAnalyze will check plan limits and handle signal deduplication
      await handleAnalyze(url);
    } catch (err) {
      console.error('ÔØî Error loading video:', err);
      setError(err.message || 'Failed to load video');
      setLoading(false);
    }
  };

  // Handle analyzing a video (ONLY called when cache is not found)
  const handleAnalyze = async (url = null, isNewAnalysis = false) => {
    const urlToAnalyze = url || videoUrl;
    console.log(`ÔÜÖ´©Å handleAnalyze called (NEW ANALYSIS) for: ${urlToAnalyze}`);
    
    if (!urlToAnalyze.trim()) {
      setError('Please enter a YouTube URL');
      return;
    }

    if (!isValidYouTubeInput(urlToAnalyze)) {
      setError('Invalid YouTube URL or video ID');
      return;
    }

    // Check plan limits (only when actually analyzing, not when loading from cache)
    if (currentUser?.uid) {
      try {
        const limitCheck = await canAnalyzeVideo(currentUser.uid);
        if (!limitCheck.allowed) {
          setError(limitCheck.error || 'You have reached your plan limit. Please upgrade to analyze more videos.');
          setLoading(false);
          return;
        }
      } catch (limitError) {
        console.error('Error checking plan limits:', limitError);
        setError('Failed to verify plan limits. Please try again.');
        setLoading(false);
        return;
      }
    }

    const videoId = extractVideoId(urlToAnalyze);
    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;

    if (!apiKey) {
      setError('YouTube API key is not configured');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysis(null);
    setVideoMetadata(null);
    setAnalysisDate(null);
    setComments([]);

    try {
      // CRITICAL: Fast check if signals exist BEFORE fetching comments or analyzing
      // This prevents redundant API calls and analysis even if cache check fails
      console.log(`­ƒöì [PRE-CHECK ${HOMEPAGE_VERSION}] Fast check for existing signals BEFORE fetching comments for video: ${videoId}`);
      const { hasSignalsForVideo } = await import('../services/learningSystem/commentSignalsService');
      const signalsExist = await hasSignalsForVideo(videoId).catch(err => {
        console.warn(`ÔÜá´©Å [PRE-CHECK ${HOMEPAGE_VERSION}] Error checking signals, proceeding with analysis:`, err);
        return false;
      });
      
      if (signalsExist) {
        console.log(`ÔÜí [PRE-CHECK ${HOMEPAGE_VERSION}] Signals exist for this video - attempting to load from cache...`);
        
        // Try to load from Firestore cache (in case localStorage cache check failed)
        const firestoreCache = await loadAnalysis(currentUser.uid, videoId);
        if (firestoreCache && firestoreCache.analysis && firestoreCache.videoMetadata) {
          // Validate cached data before using it
          const validated = validateCachedData(firestoreCache);
          if (validated) {
            console.log(`Ô£à [PRE-CHECK ${HOMEPAGE_VERSION}] Successfully loaded from Firestore cache - SKIPPING YouTube API call and analysis`);
            setAnalysis(validated.analysis);
            setVideoMetadata(validated.videoMetadata);
            setComments(validated.comments || []);
            setAnalysisDate(validated.analyzedAt ? new Date(validated.analyzedAt) : new Date());
            setActiveTab('buyers');
            setLoading(false);
            return; // Skip everything - no API calls, no analysis
          } else {
            console.warn(`ÔØî [PRE-CHECK ${HOMEPAGE_VERSION}] Firestore cache failed validation, proceeding with analysis`);
          }
        } else {
          // Signals exist but cache is missing - proceed with analysis anyway
          // The learning system will deduplicate signals, so no redundant writes will occur
          console.warn(`ÔÜá´©Å [PRE-CHECK ${HOMEPAGE_VERSION}] Signals exist but cache is missing - proceeding with analysis (signals will be deduplicated)`);
        }
      } else {
        console.log(`Ôä╣´©Å [PRE-CHECK ${HOMEPAGE_VERSION}] No signals found - proceeding with full analysis`);
      }

      // Fetch comments from YouTube (only if no signals found)
      const result = await fetchVideoComments(videoId, apiKey, 100);
      const { comments: fetchedComments, videoMetadata: metadata } = result;
      
      if (fetchedComments.length === 0) {
        setError('No comments found for this video');
        setLoading(false);
        return;
      }

      // Analyze comments
      const channelId = metadata.channelId || videoId;
      let analyzed;
      try {
        analyzed = await analyzeCommentsWithLearning(
          fetchedComments,
          videoId,
          channelId,
          metadata.commentCount
        );
        
        if (!analyzed) {
          throw new Error('Analysis returned no results');
        }
      } catch (analysisError) {
        console.error('Error during analysis:', analysisError);
        throw new Error(`Analysis failed: ${analysisError.message || 'Unknown error'}`);
      }

      setAnalysis(analyzed);
      setVideoMetadata(metadata);
      setComments(fetchedComments);
      setAnalysisDate(new Date());
      setActiveTab('buyers');

      // CRITICAL: Save analysis to cache (MUST succeed - cache is essential for performance)
      if (currentUser?.uid) {
        try {
          console.log(`­ƒÆ¥ [CACHE SAVE ${HOMEPAGE_VERSION}] Saving analysis to cache for video: ${videoId}`);
          await saveAnalysis(currentUser.uid, videoId, analyzed, metadata, fetchedComments);
          console.log(`Ô£à [CACHE SAVE ${HOMEPAGE_VERSION}] Analysis cached successfully for ${videoId}`);
          
          // Verify cache was saved
          const verifyCache = await loadAnalysis(currentUser.uid, videoId);
          if (verifyCache && verifyCache.analysis && verifyCache.videoMetadata) {
            console.log(`Ô£à [CACHE VERIFY ${HOMEPAGE_VERSION}] Cache verified - analysis and metadata present`);
          } else {
            console.error(`ÔØî [CACHE VERIFY ${HOMEPAGE_VERSION}] WARNING: Cache save may have failed - verification failed!`);
            console.error(`ÔØî [CACHE VERIFY ${HOMEPAGE_VERSION}] Cache check: ${!!verifyCache}, Analysis: ${!!verifyCache?.analysis}, Metadata: ${!!verifyCache?.videoMetadata}`);
          }
        } catch (cacheError) {
          console.error(`ÔØî [CACHE SAVE ${HOMEPAGE_VERSION}] CRITICAL: Failed to cache analysis:`, cacheError);
          console.error(`ÔØî [CACHE SAVE ${HOMEPAGE_VERSION}] This will cause performance issues on next load!`);
          // Don't block the UI - analysis is still shown, but log the error prominently
        }
      }

      // Save to history
      if (currentUser?.uid) {
        try {
          await saveToHistory(currentUser.uid, urlToAnalyze, {
            videoId: metadata.videoId,
            videoTitle: metadata.videoTitle,
            channelName: metadata.channelName,
            thumbnailUrl: metadata.thumbnailUrl,
            viewCount: metadata.viewCount,
            likeCount: metadata.likeCount,
            duration: metadata.duration,
            commentCount: metadata.commentCount,
          });
          
          // Navigate to video analysis page after analysis completes
          navigate(`/video/${videoId}`);
        } catch (historyError) {
          console.error('Failed to save to history:', historyError);
          // Still navigate even if save fails
          navigate(`/video/${videoId}`);
        }
      } else {
        // Navigate to video analysis page even if not logged in (though this shouldn't happen)
        navigate(`/video/${videoId}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch comments. Please check your API key and try again.');
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAnalyze();
    }
  };

  // Pagination
  const safeComments = Array.isArray(comments) ? comments : [];
  const totalPages = Math.ceil(safeComments.length / commentsPerPage);
  const startIndex = (currentPage - 1) * commentsPerPage;
  const endIndex = startIndex + commentsPerPage;
  const paginatedComments = safeComments.slice(startIndex, endIndex);

  // Handle tab switching and comment navigation
  useEffect(() => {
    const handleSwitchToAllComments = (e) => {
      setActiveTab('all');
      const commentId = e.detail.commentId;
      if (commentId && comments.length > 0) {
        const commentIndex = comments.findIndex(c => c.id === commentId);
        if (commentIndex >= 0) {
          const targetPage = Math.floor(commentIndex / commentsPerPage) + 1;
          setCurrentPage(targetPage);
          setTimeout(() => {
            const element = document.getElementById(`comment-${commentId}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      }
    };

    window.addEventListener('switchToAllComments', handleSwitchToAllComments);
    return () => window.removeEventListener('switchToAllComments', handleSwitchToAllComments);
  }, [comments, commentsPerPage]);

  return (
    <div 
      className="min-h-screen relative"
      style={{
        fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
        background: 'linear-gradient(135deg, #1a1b2e 0%, #2d1b3d 100%)',
        color: '#ffffff',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      {/* Content */}
      {!analysis ? (
        /* Centered Homepage - No Analysis */
        <div className="w-full" style={{ maxWidth: '700px', textAlign: 'center' }}>
          {/* Title */}
          <h1 
            className="homepage-title-responsive"
            style={{
              fontSize: '56px',
              fontWeight: 700,
              marginBottom: '16px',
              letterSpacing: '-0.02em',
              color: '#ffffff',
              fontStyle: 'normal',
              fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
            }}
          >
            SocialRadar
          </h1>
          
          {/* Subtitle */}
          <p 
            className="homepage-subtitle-responsive"
            style={{
              fontSize: '20px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '48px',
              fontWeight: 400,
              fontStyle: 'normal'
            }}
          >
            The revenue layer for social comments
          </p>
          
          {/* URL Input */}
          <div style={{ marginBottom: '16px', position: 'relative', width: '100%' }}>
            <FiYoutube 
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#ffffff',
                fontSize: '20px',
                pointerEvents: 'none',
                zIndex: 1
              }}
            />
            <input
              type="text"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter YouTube video URL (e.g., https://www.youtube.com/watch?v=VIDEO_ID)"
              style={{
                width: '100%',
                padding: '16px 16px 16px 48px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '16px', // fontSizes.base
                outline: 'none',
                transition: 'all 0.3s',
                fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
                fontStyle: 'normal',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                e.target.style.borderColor = 'rgba(124, 58, 237, 0.5)';
              }}
              onBlur={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              }}
            />
          </div>

          {/* Analyze Button */}
          <button
            onClick={() => handleAnalyze()}
            disabled={loading || !videoUrl.trim()}
            style={{
              width: '100%',
              padding: '16px 32px',
              background: '#5432FF',
              border: 'none',
              borderRadius: '12px',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: 600,
              cursor: loading || !videoUrl.trim() ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              boxShadow: '0 4px 20px rgba(84, 50, 255, 0.3)',
              opacity: loading || !videoUrl.trim() ? 0.5 : 1,
              fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
              boxSizing: 'border-box'
            }}
            onMouseEnter={(e) => {
              if (!loading && videoUrl.trim()) {
                e.target.style.background = '#4328cc';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 24px rgba(84, 50, 255, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && videoUrl.trim()) {
                e.target.style.background = '#5432FF';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 20px rgba(84, 50, 255, 0.3)';
              }
            }}
            onMouseDown={(e) => {
              if (!loading && videoUrl.trim()) {
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            {loading ? 'Analyzing...' : 'Analyze Comments'}
          </button>

          {error && (
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
                background: '#fee2e2', // error.light
                border: '1px solid #ef4444', // error.base
                borderRadius: '12px', // borderRadius.lg
                color: '#991b1b', // error.dark
                marginTop: '24px'
              }}
            >
              <AlertTriangle size={20} />
              <span>{error}</span>
            </div>
          )}
        </div>
      ) : (
        /* Analysis View - Full Width */
        <div className="relative z-10 min-h-screen px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Header for analysis view */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Geist', sans-serif" }}>
                YouTube Comment Analyzer
              </h1>
              
              {/* URL Input */}
              <div className="flex gap-4 mb-4">
                <input
                  type="text"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter YouTube URL or Video ID"
                  className="flex-1 px-4 py-3 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ fontFamily: "'Geist', sans-serif", fontStyle: 'normal' }}
                />
                <button
                  onClick={() => handleAnalyze()}
                  disabled={loading || !videoUrl.trim()}
                  className="px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ fontFamily: "'Geist', sans-serif" }}
                >
                  {loading ? 'Analyzing...' : 'Analyze'}
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-200">
                  <AlertTriangle size={20} />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !analysis && (
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mb-4"></div>
          <p className="text-white text-opacity-70">Analyzing comments...</p>
        </div>
      )}

      {/* Results */}
      {analysis && videoMetadata && typeof analysis === 'object' && (
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <VideoInfo videoMetadata={videoMetadata} analysisDate={analysisDate} />
          
          {analysis && (
            <SummaryPanel
              analysis={analysis}
              comments={comments}
              videoMetadata={videoMetadata}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          )}

          {/* Comments List - White Background */}
          <div className="mt-8 bg-white rounded-2xl p-6">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Geist', sans-serif", color: '#1f2937' }}> {/* text.primary.dark */}
              All Comments ({safeComments.length})
            </h2>
            
            {paginatedComments.map((comment, index) => {
              if (!comment || typeof comment !== 'object') {
                console.warn('Invalid comment at index', index, comment);
                return null;
              }
              return <CommentCard key={comment.id || `comment-${index}`} comment={comment} />;
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-white">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* UpgradeModal removed - not needed on homepage as it redirects to video analysis page */}
    </div>
  );
};

export default HomePage;
